import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import React, { useState } from "react";
import useAuthModal from "../../hooks/useAuthModal";
import { getAuth } from "firebase/auth";
import axios from "axios";
import { formatPriceWithLocale, getPriceId } from "../../utils/pricing";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaTrigger,
} from "@jammable/ui-components/Dialog";
import { Upgrade } from "@jammable/ui-payments-feature";
import { useRouter } from "next/router";
import { FaCheck, FaCheckCircle } from "react-icons/fa";

const PricingCard = ({
  plan,
  status,
  currency,
  price,
  originalPrice,
  benefits,
  setClientSecret,
  onSelect,
}) => {
  const isBasic = plan === "basic";
  const isCreator = plan === "creator";
  const { checkAuthAndExecute } = useAuthModal();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const redirectToManageSubscription = async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    axios
      .post(`/api/payment/manageSubscription`, undefined, {
        headers: { Authorization: token },
      })
      .then((res) => (window.location.href = res.data.url))
      .catch(() =>
        toast.error("Something went wrong, please contact support!"),
      );
  };

  return (
    <div
      className={`tw-w-full tw-h-full ${
        isBasic
          ? "tw-text-black tw-bg-white tw-border-3 tw-border-solid tw-border-black tw-shadow-md"
          : "tw-bg-purple-600 tw-text-white tw-shadow-lg"
      } tw-rounded-3xl tw-p-6 tw-flex tw-flex-col`}
    >
      <div className="tw-flex tw-items-center">
        <h2 className="tw-bg-black tw-w-max tw-p-2 tw-rounded-xl tw-px-3  tw-my-0">
          <div className="tw-text-white tw-text-xl tw-font-black">
            {isBasic ? "BASIC" : "CREATOR"}
          </div>
          <div className="tw-text-xs tw-text-white tw-uppercase tw-font-black tw-tracking-wide">
            First month {isBasic ? "75%" : "60%"} off
          </div>
        </h2>

        {isCreator && (
          <div className="tw-text-sm tw-bg-lime-300 tw-text-black tw-font-black tw-py-1.5 tw-px-2 tw-rounded-r-xl tw-flex tw-items-center">
            MOST POPULAR <span className="tw-ml-1">🔥</span>
          </div>
        )}
      </div>
      <div className="tw-text-sm tw-mt-3 tw-font-bold tw-uppercase tw-tracking-widest tw-text-opacity-70">
        {isBasic
          ? "Perfect for getting started"
          : "Join thousands of creators on the best value subscription"}
      </div>
      <div className="tw-flex tw-flex-col tw-mt-4">
        <div className="tw-font-bold tw-line-through tw-text-lg tw-opacity-60">
          {formatPriceWithLocale(originalPrice, currency)}
        </div>
        <div>
          <span className="tw-text-5xl tw-font-black">
            {formatPriceWithLocale(price, currency)}
          </span>
          <span className="tw-text-sm tw-font-semibold">/ mo</span>
        </div>
        <div className="tw-flex tw-gap-3 tw-flex-wrap tw-mt-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className={`tw-p-2 tw-rounded-lg ${benefit.color} tw-text-sm tw-font-black tw-uppercase tw-text-black tw-tracking-wide tw-flex tw-items-center tw-transition-all tw-duration-200 hover:tw-opacity-90`}
            >
              {benefit.title}
            </div>
          ))}
        </div>
      </div>

      {status === "basic" && isCreator ? (
        <Credenza>
          <CredenzaTrigger asChild>
            <SecondaryButton
              className={`tw-mt-auto tw-font-black tw-border-none tw-w-full  tw-text-black tw-mt-10 tw-text-xl tw-h-14 tw-rounded-2xl tw-flex tw-items-center tw-justify-center ${
                isBasic ? "tw-hidden" : ""
              }`}
            >
              UPGRADE
            </SecondaryButton>
          </CredenzaTrigger>
          <CredenzaContent>
            <CredenzaBody>
              <Upgrade
                type="Jammable Creator"
                fallback={() => redirectToManageSubscription()}
                callback={() => {
                  toast.success("Your subscription has been updated!");
                  setTimeout(() => router.push("/settings"), 1500);
                }}
              />
            </CredenzaBody>
          </CredenzaContent>
        </Credenza>
      ) : (
        <Button
          onClick={async () => {
            if (!checkAuthAndExecute()) return false;
            if (status === "basic" && isBasic) return false;
            if (status === "creator" && isCreator) return false;

            const priceId = getPriceId(plan, currency);

            if (onSelect) {
              onSelect();
            }

            setLoading(true);

            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();

            axios
              .post(
                `/api/payment/createSubscriptionIntent`,
                {
                  priceId: priceId,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  refId: window?.tolt_referral,
                  currency: currency,
                  from: "pricing-page",
                },
                { headers: { Authorization: token } },
              )
              .then((res) => {
                setClientSecret(res.data.clientSecret);
                setTimeout(() => {
                  setLoading(false);
                }, 0);
              })
              .catch(() => {
                toast.error("Something went wrong. Please contact support!");
                setLoading(false);
              });
          }}
          className={`tw-mt-auto tw-font-black tw-border-none tw-w-full  tw-text-black tw-mt-8 tw-text-xl tw-h-14 tw-rounded-2xl tw-flex tw-items-center tw-justify-center ${
            isBasic
              ? "tw-bg-black tw-text-white hover:tw-bg-black/90"
              : "tw-bg-white tw-text-black hover:tw-bg-white/90"
          }`}
        >
          {loading ? (
            <Spinner
              size={4}
              className={`tw-border-3 ${!isBasic ? "tw-border-black" : "tw-border-white"}`}
            />
          ) : (isBasic && status === "basic") ||
            (isCreator && status === "creator") ? (
            <>
              <FaCheckCircle className="tw-text-green-400" />
              ACTIVE
            </>
          ) : (
            "GET STARTED"
          )}
        </Button>
      )}
    </div>
  );
};

export default PricingCard;
